import React, { useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import { Badge, Button, styles as ui, TextField } from "stories/Admin";
import { KIDS_API, KidsDua } from "./kidsTypes";
import styles from "./KidsPage.module.scss";

interface Props {
  selected: KidsDua | null;
  onSelect: (dua: KidsDua) => void;
}

/** Søk i duaene (tittel, kategori, oversettelse, arabisk eller id) og vis den valgte. */
const DuaPicker: React.FC<Props> = ({ selected, onSelect }) => {
  const search = useFetch<KidsDua[]>();
  const searchRef = useRef(search.execute);
  searchRef.current = search.execute;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<KidsDua[]>([]);
  const [open, setOpen] = useState(!selected);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(async () => {
      const found = await searchRef.current(
        `${KIDS_API}/duas`,
        "GET",
        undefined,
        {
          q: query.trim() || undefined,
          limit: 20,
        },
      );
      setResults(found ?? []);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, open]);

  return (
    <div className={styles.duaPicker}>
      {selected && (
        <div className={styles.duaSelected}>
          <div className={styles.duaSelectedHead}>
            <b>{selected.tittel || `Dua ${selected.id}`}</b>
            <Badge tone="grey">
              <span className={ui.num}>#{selected.id}</span>
            </Badge>
            {selected.kategori && (
              <Badge tone="blue">{selected.kategori}</Badge>
            )}
            {!selected.audioUrl && <Badge tone="gold">Ingen lyd</Badge>}
            <span className={styles.spacer} />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setOpen(!open)}
            >
              {open ? "Lukk søket" : "Bytt dua"}
            </Button>
          </div>
          <p className={`${ui.arabic} ${styles.duaArabic}`} lang="ar" dir="rtl">
            {selected.arabisk1 || selected.arabisk}
          </p>
          {selected.transliteration && (
            <p className={styles.duaLine}>
              <i>{selected.transliteration}</i>
            </p>
          )}
          {selected.oversettelse && (
            <p className={styles.duaLine}>{selected.oversettelse}</p>
          )}
          {selected.referanse && (
            <p className={styles.duaRef}>{selected.referanse}</p>
          )}
        </div>
      )}
      {open && (
        <>
          <TextField
            label={selected ? "Søk etter en annen dua" : "Søk etter dua"}
            placeholder="F.eks. «spiser», «سفر» eller et id-nummer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul className={styles.duaResults} aria-busy={search.isLoading}>
            {results.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className={styles.duaResult}
                  aria-pressed={selected?.id === d.id}
                  onClick={() => {
                    onSelect(d);
                    setOpen(false);
                  }}
                >
                  <span className={styles.duaResultHead}>
                    <b>{d.tittel || `Dua ${d.id}`}</b>
                    <span className={styles.duaResultMeta}>
                      {d.kategori} · #{d.id}
                      {!d.audioUrl && " · ingen lyd"}
                    </span>
                  </span>
                  <span
                    className={`${ui.arabic} ${styles.duaResultArabic}`}
                    lang="ar"
                    dir="rtl"
                  >
                    {(d.arabisk1 || d.arabisk || "").slice(0, 90)}
                  </span>
                </button>
              </li>
            ))}
            {!search.isLoading && results.length === 0 && (
              <li className={styles.muted}>Ingen duaer funnet.</li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default DuaPicker;
